import { useState, useEffect, useRef } from 'react';
import { useSignalRContext } from '../SignalR/useSignalR';

const ALERTS_STORAGE_KEY = 'systemAlerts';

export const useFixLogs = (externalLogsList) => {
  // Accept logsList as argument, fallback to SignalR context
  const context = useSignalRContext();
  const logsList = externalLogsList || context.logsList;
  const [metrics, setMetrics] = useState(null);
  const [alerts, setAlerts] = useState([]);
  const [isConnected, setIsConnected] = useState(false);
  const [loading, setLoading] = useState(true);

  // Session-persistent latency state
  const latencyHistoryRef = useRef([]); // array of latencies
  const latencyAvgRef = useRef(0); // rolling average
  const lastSessionIdRef = useRef(null);

  useEffect(() => {
    if (!logsList || logsList.length === 0) {
      setMetrics(null);
      setIsConnected(false);
      setLoading(false);
      latencyHistoryRef.current = [];
      latencyAvgRef.current = 0;
      lastSessionIdRef.current = null;
      return;
    }
    // --- Metric Calculation ---
    let ordersProcessed = 0, sessionId = '', uptime = '', lastLoginTime = null, lastExecTime = null, sequence = '--', serverName = 'cServer.TRADE';
    let heartbeat = 'unknown', connectionStatus = 'disconnected';
    let msgCount = 0, errorCount = 0;
    let now = Date.now();
    let firstLogTime = null;
    let sentHeartbeats = {};
    let newAlerts = [];
    let spikeDetected = false;
    // Find sessionId from latest LOGIN
    for (let i = logsList.length - 1; i >= 0; i--) {
      const log = logsList[i].log || logsList[i];
      if (log && log.messageType === 'LOGIN') {
        sessionId = log.sessionId || '--';
        break;
      }
    }
    // Reset latency history if session changed (logout/login)
    if (lastSessionIdRef.current !== sessionId) {
      latencyHistoryRef.current = [];
      latencyAvgRef.current = 0;
      lastSessionIdRef.current = sessionId;
    }
    logsList.slice(-1000).forEach((entry) => {
      const log = entry.log || entry;
      if (!log) return;
      const { time, messageType, type, direction, sequence: seq } = log;
      msgCount++;
      if (!firstLogTime && time) firstLogTime = new Date(time).getTime();
      // Heartbeat latency calculation
      if (messageType === 'HEARTBEAT') {
        heartbeat = 'healthy';
        if (direction === 'Sent') {
          sentHeartbeats[seq || time] = time;
        } else if (direction === 'Received' && sentHeartbeats[seq || time]) {
          const sentTime = sentHeartbeats[seq || time];
          const latency = new Date(time) - new Date(sentTime);
          if (latency > 0 && latency < 10000) {
            latencyHistoryRef.current.push(latency);
            if (latencyHistoryRef.current.length > 100) latencyHistoryRef.current.shift();
            // Rolling average
            const sum = latencyHistoryRef.current.reduce((a, b) => a + b, 0);
            latencyAvgRef.current = Math.round(sum / latencyHistoryRef.current.length);
            // Spike detection
            if (
              latencyHistoryRef.current.length > 1 &&
              latency > latencyAvgRef.current + 30 &&
              !spikeDetected
            ) {
              newAlerts.push({
                type: 'yellow',
                message: `Latency spike detected: ${latency}ms (avg ${latencyAvgRef.current}ms)` ,
                time: new Date().toLocaleTimeString()
              });
              spikeDetected = true;
            }
          }
        }
      }
      // Login
      if (messageType === 'LOGIN') { lastLoginTime = new Date(time); connectionStatus = 'connected'; }
      // Execution report
      if (messageType === 'EXECUTION_REPORT') { ordersProcessed++; lastExecTime = time; }
      // Sequence
      if (log.sequence) sequence = log.sequence;
      // Server name
      if (log.serverName) serverName = log.serverName;
      // Error
      if (type && type.toUpperCase() === 'ERROR') errorCount++;
      // Position report alert
      if (messageType === 'POSITION_REPORT') {
        newAlerts.push({
          type: 'position',
          message: 'Position report updated',
          time: time ? time.slice(11, 19) : new Date().toLocaleTimeString([], { hour12: false })
        });
      }
    });
    // Uptime
    if (lastLoginTime) {
      const diff = now - lastLoginTime.getTime();
      const h = Math.floor(diff / 3600000).toString().padStart(2, '0');
      const m = Math.floor((diff % 3600000) / 60000).toString().padStart(2, '0');
      const s = Math.floor((diff % 60000) / 1000).toString().padStart(2, '0');
      uptime = `${h}:${m}:${s}`;
    } else {
      uptime = '--:--:--';
    }
    // Set metrics
    const latencyArr = latencyHistoryRef.current;
    const latency = {
      current: latencyArr.length > 0 ? latencyArr[latencyArr.length - 1] : 0,
      average: latencyAvgRef.current,
      max: latencyArr.length > 0 ? Math.max(...latencyArr) : 0,
      min: latencyArr.length > 0 ? Math.min(...latencyArr) : 0,
      history: latencyArr.slice(-12)
    };
    setMetrics({
      latency,
      heartbeat,
      ordersProcessed,
      uptime,
      sessionId,
      lastExecTime: lastExecTime ? lastExecTime.slice(11, 16) : '--:--',
      sequence,
      serverName,
      connectionStatus,
      messagesPerMinute: Math.round(msgCount / ((logsList.length > 0 && firstLogTime ? (now - firstLogTime) : 60000) / 60000)),
      errorRate: msgCount ? (errorCount / msgCount) * 100 : 0
    });
    setAlerts(newAlerts);
    setIsConnected(connectionStatus === 'connected');
    setLoading(false);
  }, [logsList]);

  return { metrics, alerts, isConnected, loading };
}; 